import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { trackCTAClickEvent } from './useIntentTracking';
import { isDoNotTrack } from '@/lib/doNotTrack';
import { hasConsent as hasCategoryConsent, onConsentChange } from '@/lib/consent';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Statistics consent (GA4) — also false under DNT/GPC or internal opt-out
const hasConsent = (): boolean => {
  if (isDoNotTrack()) return false;
  return hasCategoryConsent('statistics');
};

// Fetch GA Measurement ID from database
const fetchMeasurementId = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', 'ga_measurement_id')
      .single();
    
    if (error || !data) return null;
    return data.value;
  } catch {
    return null;
  }
};

// Initialize GA4 with dynamic measurement ID
const initializeGA = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  
  // Only with statistics consent (and no DNT/GPC)
  if (!hasConsent()) return false;
  
  // Check if already initialized
  if ((window as unknown as { GA_INITIALIZED?: boolean }).GA_INITIALIZED) {
    return true;
  }

  const measurementId = await fetchMeasurementId();
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    console.warn('GA4: No valid measurement ID configured');
    return false;
  }

  return new Promise((resolve) => {
    // Initialize dataLayer and gtag function BEFORE loading script
    window.dataLayer = window.dataLayer || [];
    // gtag.js only processes `arguments` objects, not plain arrays.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    } as (...args: unknown[]) => void;

    // Add gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    
    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        anonymize_ip: true, // GDPR compliance
        cookie_flags: 'SameSite=None;Secure',
        send_page_view: true,
      });

      // Store measurement ID and mark as initialized
      (window as unknown as { GA_MEASUREMENT_ID: string }).GA_MEASUREMENT_ID = measurementId;
      (window as unknown as { GA_INITIALIZED: boolean }).GA_INITIALIZED = true;
      
      console.log('GA4: Initialized with ID', measurementId);
      resolve(true);
    };

    script.onerror = () => {
      console.error('GA4: Failed to load gtag script');
      resolve(false);
    };

    document.head.appendChild(script);
  });
};

// Get stored measurement ID
const getStoredMeasurementId = (): string | null => {
  return (window as unknown as { GA_MEASUREMENT_ID?: string }).GA_MEASUREMENT_ID || null;
};

// Track page view
const trackPageView = (path: string) => {
  if (!hasConsent() || !window.gtag) return;
  
  const measurementId = getStoredMeasurementId();
  if (!measurementId) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

// Track custom event
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!hasConsent() || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Convenience tracking functions
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('click', 'Button', `${buttonName}${location ? ` - ${location}` : ''}`);
};

export const trackCTAClick = (ctaName: string, destination?: string) => {
  // GA4 tracking (with consent)
  trackEvent('cta_click', 'CTA', `${ctaName}${destination ? ` -> ${destination}` : ''}`);
  
  // First-party reach measurement (only with statistics consent; checked inside)
  trackCTAClickEvent(ctaName, destination);
};

export const trackFormSubmission = (formName: string, success: boolean = true) => {
  trackEvent(success ? 'form_submit_success' : 'form_submit_error', 'Form', formName);
};

export const trackNavigation = (menuItem: string) => {
  trackEvent('navigation', 'Menu', menuItem);
};

export const trackOutboundLink = (url: string) => {
  trackEvent('outbound_click', 'External Link', url);
};

export const trackSocialClick = (platform: string) => {
  trackEvent('social_click', 'Social Media', platform);
};

export const trackDownload = (fileName: string) => {
  trackEvent('download', 'File', fileName);
};

export const trackSearch = (searchTerm: string) => {
  trackEvent('search', 'Site Search', searchTerm);
};

export const trackLanguageSwitch = (fromLang: string, toLang: string) => {
  trackEvent('language_switch', 'Localization', `${fromLang} -> ${toLang}`);
};

// Hook to initialize and track page views
export const useAnalytics = () => {
  const location = useLocation();

  // Initialize GA on mount if consent given
  useEffect(() => {
    if (hasConsent()) {
      initializeGA();
    }

    // Listen for consent changes
    return onConsentChange(() => {
      if (hasConsent()) {
        initializeGA().then(() => {
          trackPageView(location.pathname);
        });
      }
    });
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Return track functions for manual event tracking
  return {
    trackEvent: useCallback(trackEvent, []),
    trackButtonClick: useCallback(trackButtonClick, []),
    trackCTAClick: useCallback(trackCTAClick, []),
    trackFormSubmission: useCallback(trackFormSubmission, []),
    trackNavigation: useCallback(trackNavigation, []),
    trackOutboundLink: useCallback(trackOutboundLink, []),
    trackSocialClick: useCallback(trackSocialClick, []),
    trackDownload: useCallback(trackDownload, []),
    trackSearch: useCallback(trackSearch, []),
    trackLanguageSwitch: useCallback(trackLanguageSwitch, []),
  };
};

// Hook to reinitialize analytics after consent
export const useConsentAnalytics = () => {
  const location = useLocation();

  const onConsentAccepted = useCallback(() => {
    initializeGA().then(() => {
      trackPageView(location.pathname);
    });
  }, [location.pathname]);

  return { onConsentAccepted };
};

export default useAnalytics;
